import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../api";
import { LOGO_URL, BRAND } from "../brand";
import { CheckCircle2, Sparkles } from "lucide-react";

const EMPTY = {
  full_name:"", cpf:"", company_name:"", category:"Manicure",
  specialties:"", experience_years:"", region:"",
  services:"", prices:"", career_goals:"",
  photos:[], videos:[],
  heard_about:"", contact_email:"", contact_phone:""
};

export default function SejaParceiroPage() {
  const [form, setForm] = useState(EMPTY);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [photoInput, setPhotoInput] = useState("");
  const [videoInput, setVideoInput] = useState("");

  const addUrl = (field, val, clear) => {
    if (!val || !/^https?:\/\//i.test(val)) return toast.error("Cole uma URL válida");
    setForm({ ...form, [field]: [...form[field], val] });
    clear("");
  };
  const removeUrl = (field, i) => setForm({ ...form, [field]: form[field].filter((_,idx)=>idx!==i) });

  const submit = async () => {
    if (!form.full_name || !form.contact_email || !form.category) return toast.error("Nome, email e categoria são obrigatórios");
    setBusy(true);
    try {
      await axios.post(`${API}/partner-applications`, form);
      setSubmitted(true);
      toast.success("Solicitação enviada!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao enviar");
    } finally { setBusy(false); }
  };

  if (submitted) {
    return (
      <div className="max-w-xl mx-auto px-6 py-20 text-center">
        <div className="card-arya p-10">
          <div className="w-16 h-16 mx-auto rounded-full bg-[rgba(74,124,62,0.15)] grid place-items-center text-[color:var(--success)]">
            <CheckCircle2 size={32}/>
          </div>
          <h1 className="font-serif text-3xl mt-5">Cadastro realizado com sucesso!</h1>
          <p className="text-[color:var(--ink-500)] mt-3">
            Sua solicitação será analisada pela nossa equipe. Você será informado quando houver uma atualização pelo email <b>{form.contact_email}</b>.
          </p>
          <Link to="/" className="btn-gold mt-6 inline-flex items-center gap-2" data-testid="btn-back-home"><Sparkles size={14}/> Voltar ao início</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-14">
      <div className="text-center mb-10">
        <div className="tag-sun mb-3 inline-block">SEJA PARCEIRO</div>
        <h1 className="font-serif text-4xl sm:text-5xl">Faça parte do Arya Studio</h1>
        <p className="text-[color:var(--ink-500)] mt-3">Preencha o formulário abaixo. Sua solicitação será analisada pela nossa equipe.</p>
      </div>

      <div className="card-arya p-8 space-y-4">
        <Section title="Dados pessoais">
          <Field label="Nome completo *" value={form.full_name} onChange={v=>setForm({...form, full_name:v})} testid="sp-name"/>
          <Field label="CPF (quando aplicável)" value={form.cpf} onChange={v=>setForm({...form, cpf:v})}/>
          <Field label="Email de contato *" type="email" value={form.contact_email} onChange={v=>setForm({...form, contact_email:v})} testid="sp-email"/>
          <Field label="Telefone / WhatsApp" value={form.contact_phone} onChange={v=>setForm({...form, contact_phone:v})}/>
        </Section>

        <Section title="Negócio">
          <Field label="Nome da empresa (se tiver)" value={form.company_name} onChange={v=>setForm({...form, company_name:v})}/>
          <Field label="Categoria profissional *" value={form.category} onChange={v=>setForm({...form, category:v})} testid="sp-category"/>
          <Field label="Especialidades" value={form.specialties} onChange={v=>setForm({...form, specialties:v})}/>
          <Field label="Tempo de experiência profissional" value={form.experience_years} onChange={v=>setForm({...form, experience_years:v})}/>
          <Field label="Localização e região de atendimento" value={form.region} onChange={v=>setForm({...form, region:v})}/>
        </Section>

        <Section title="Serviços">
          <Field label="Serviços oferecidos" value={form.services} onChange={v=>setForm({...form, services:v})} textarea/>
          <Field label="Preços dos serviços" value={form.prices} onChange={v=>setForm({...form, prices:v})} textarea/>
          <Field label="Plano de carreira / objetivos" value={form.career_goals} onChange={v=>setForm({...form, career_goals:v})} textarea/>
        </Section>

        <Section title="Portfólio (URLs de fotos e vídeos)">
          <UrlList label="Fotos de trabalhos" input={photoInput} setInput={setPhotoInput}
                   onAdd={() => addUrl("photos", photoInput, setPhotoInput)} items={form.photos}
                   onRemove={(i)=>removeUrl("photos", i)}/>
          <UrlList label="Vídeos demonstrativos" input={videoInput} setInput={setVideoInput}
                   onAdd={() => addUrl("videos", videoInput, setVideoInput)} items={form.videos}
                   onRemove={(i)=>removeUrl("videos", i)}/>
          <p className="text-[10px] text-[color:var(--ink-500)]">Cole URLs públicas (Instagram, Google Drive compartilhado, YouTube, etc).</p>
        </Section>

        <Section title="Como você nos conheceu?">
          <Field label="Como ficou sabendo da plataforma?" value={form.heard_about} onChange={v=>setForm({...form, heard_about:v})} textarea/>
        </Section>

        <button onClick={submit} disabled={busy} className="btn-gold w-full" data-testid="btn-sp-submit">
          {busy ? "Enviando..." : "Enviar solicitação"}
        </button>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <p className="text-[10px] tracking-widest font-display text-[color:var(--sun-700)] mb-2">{title.toUpperCase()}</p>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
function Field({ label, value, onChange, type="text", textarea=false, testid }) {
  return (
    <div>
      <label className="text-xs text-[color:var(--ink-500)]">{label}</label>
      {textarea ? (
        <textarea rows="2" value={value||""} onChange={e=>onChange(e.target.value)}
                  data-testid={testid}
                  className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm"/>
      ) : (
        <input type={type} value={value||""} onChange={e=>onChange(e.target.value)}
               data-testid={testid}
               className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm"/>
      )}
    </div>
  );
}
function UrlList({ label, input, setInput, onAdd, items, onRemove }) {
  return (
    <div>
      <label className="text-xs text-[color:var(--ink-500)]">{label}</label>
      <div className="flex gap-2">
        <input value={input} onChange={e=>setInput(e.target.value)} placeholder="https://..."
               className="flex-1 rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm"/>
        <button onClick={onAdd} className="btn-gold !py-2 !px-4 text-sm">Adicionar</button>
      </div>
      {items.length > 0 && (
        <ul className="mt-2 space-y-1">
          {items.map((u, i) => (
            <li key={i} className="flex items-center gap-2 text-xs text-[color:var(--ink-500)]">
              <span className="truncate flex-1">{u}</span>
              <button onClick={()=>onRemove(i)} className="text-[color:var(--danger)]">remover</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
